import { UUIDv4 } from "@heliopolis/schema/uuid.js";
import * as z from "zod/v4";
import { Logger } from "./services/logger.ts";

export const ApplicationMode = z.enum({
  /** Enabled by default for additional logging, and debugging tools */
  Development: "development",
  /** All debugging tools disabled */
  Production: "production",
});
export type ApplicationMode = z.output<typeof ApplicationMode>;

const configurationSchema = z.object({
  /**
   * Generally, 'development' is used by default to enable more logging and
   * developer utilities
   *
   * @default "development"
   */
  mode: ApplicationMode.meta({
    description:
      "Generally, 'development' is used by default to enable more logging and developer utilities",
  })
    .optional()
    .default("development"),

  /**
   * The URL to the API server that should be used as a backend
   *
   * @default `${window.location.origin ?? "http://localhost:3000"}/api`
   */
  apiURL: z
    .url()
    .or(z.instanceof(URL))
    .meta({
      description: "The URL to the API server that should be used as a backend",
    })
    .optional()
    .default(new URL("/api", globalThis?.location?.origin ?? "http://localhost:3000").href),
});
export const Configuration = Object.assign(configurationSchema, {
  defaults: {
    mode: ApplicationMode.enum.Development,
    apiURL: new URL("/api", globalThis?.location?.origin ?? "http://localhost:3000").href,
  } as const satisfies Configuration,
});
export interface Configuration extends z.output<typeof configurationSchema> {}

export const HTTPServerConfiguration = z.object({
  /**
   * Where should the HTTP server look for its data?
   *
   * @example "file://./databases/prod.json"
   * @default "file://./databases/development.db"
   */
  databaseUrl: z.string().optional().default("file://./databases/development.db"),

  /**
   * Optional path/URL to a JSON service store used by the health endpoint.
   * This is intentionally separate from `databaseUrl`.
   *
   * @example "file://./databases/services.json"
   */
  serviceStorePath: z.string().optional(),

  /**
   * The primary logger available to API handlers and middleware.
   * While it may seem a little tedious to explicitly pass around a logger, the
   * convenience and testability is worth the effort.
   *
   * @default undefined
   * @example globalSelf.console
   */
  logger: Logger.optional(),

  /** To what port should the HTTP server be bound to receive requests? */
  port: z
    .int()
    .min(1)
    .max(65535)
    .optional()
    .meta({
      description: "The port to which the HTTP server should be bound to receive requests",
      examples: [3000, 8080],
    }),
});
export interface HTTPServerConfiguration extends z.output<typeof HTTPServerConfiguration> {}

export const HTTPServerContext = z.object({
  /** The current configuration of the server */
  configuration: HTTPServerConfiguration.nullish(),

  /**
   * A unique identifier for the {@link HTTPServer}
   *
   * @example "bfc8ce9e-a48e-435d-837a-2ee029894201"
   */
  serverId: UUIDv4,

  /** The web request that initiated the procedure call */
  webRequest: z.instanceof(Request),
});
export interface HTTPServerContext extends z.output<typeof HTTPServerContext> {}

/**
 * An application or program run as an on ongoing service
 *
 * These could be reverse proxies, HTTP servers, applilcations, worker queues,
 * etc.
 */
export const Service = z
  .object({
    name: z
      .string()
      .trim()
      .nonempty()
      .meta({ description: "The human-meaningful name for the service" }),
    entrypointURL: z.url().optional().meta({
      description: "The main entrypoint of the service. This may be used to present links.",
    }),
    healthCheckURL: z
      .url()
      .optional()
      .meta({ description: "The URL that will be sent health check requests" }),
  })
  .meta({
    description: `An application or program run as an ongoing services

These could be reverse proxies, HTTP servers, applications, worker queues, etc.`,
  });
export interface Service extends z.output<typeof Service> {}

const baseReport = z.object({
  checkedAt: z.iso.datetime().nullish(),
  ok: z.boolean(),
  service: Service,
});

export const ServiceHealthCheckReport = z.discriminatedUnion("ok", [
  baseReport.extend({ ok: z.literal(true) }),
  baseReport.extend({ error: z.string().min(1), ok: z.literal(false) }),
]);
export type ServiceHealthCheckReport = z.output<typeof ServiceHealthCheckReport>;

export const DatabaseStore = z.object({
  services: z.array(Service).nullish().meta({
    description: "The collection of all the {@link Service}s in the store ",
  }),
});
export interface DatabaseStore extends z.output<typeof DatabaseStore> {}

export const index = {
  "database-store": DatabaseStore,
  service: Service,
  "service-health-check-report": ServiceHealthCheckReport,
} as const satisfies Record<string, z.ZodTypeAny>;
